import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import VisibilitySensor from 'react-visibility-sensor';
import { Link } from 'react-router';
import VideoTile from 'components/VideoTile';
import { renderImage, getDynamicImage, MARKET_VIDEO_IMAGE } from 'helpers/ImageHelper';
import { isInternalLink } from 'helpers/ContentHelper';
import { helperGoToRelativeUrl } from 'helpers/RouteHelper';
import { Events, handleAnalytics } from '@foxcorp/delta-web-analytics';
import Style from 'app/themes/styleguide/nation/MarketingBanner.module.scss';
import Header from './Header';
import Description from './Description';
import MarketingButtonCTA from './MarketingButtonCTA';

const MarketingBanner = ({
  adjacentCollectionAbove = '',
  alternativeHeadline = '',
  autoPlayStill = null,
  autoPlayVideo = null,
  bannerDimensions = '',
  breakpoint = null,
  ctaText = '',
  headline = '',
  images = {},
  name = '',
  url = '',
}) => {
  const [isVisible, setVisible] = useState(false);
  const image = _.get(autoPlayStill, 'default.url', '');
  const video = _.get(autoPlayVideo, 'default.url', '');
  const logo = _.get(images, 'promoImage', '');

  const navigateToURL = () => {
    if (isInternalLink(url)) {
      helperGoToRelativeUrl(url);
    }
  };

  const handleBannerClick = ({ page_action_location }) => {
    const trackEventProps = _.pickBy({
      page_action_location,
      page_button_text: ctaText,
      page_collection_title: adjacentCollectionAbove,
      page_item_dimension: bannerDimensions,
      page_item_state: 'autoplay video',
      page_item_subtitle: alternativeHeadline,
      page_item_title: headline || name,
      page_item_type: 'marketing video banner',
    });

    handleAnalytics({
      event: Events.BANNER_CLICKED,
      payload: { ...trackEventProps },
      callBack: navigateToURL,
    });
  };
  if (!image && !video) return null;

  const linkProps = {
    onClick: () => {
      handleBannerClick({ page_action_location: 'whole banner' });
    },
    to: isInternalLink(url) ? null : url,
    target: isInternalLink(url) ? null : '_blank',
  };

  const markingImage = getDynamicImage({
    breakpoint,
    context: MARKET_VIDEO_IMAGE,
    url: image,
  });

  return (
    <VisibilitySensor onChange={visibility => setVisible(visibility)}>
      <Link {...linkProps}>
        <div className={Style.marketingBanner}>
          <div className={Style.contentContainer}>
            <Header image={logo} text={headline} theme={Style} />
            <Description description={alternativeHeadline} theme={Style} />
            <MarketingButtonCTA
              ctaText={ctaText}
              handleBannerClick={handleBannerClick}
              theme={Style}
              url={url}
            />
          </div>
          <div className={Style.displayContainer}>
            <div className={Style.videoImage}>
              <VideoTile
                data-test="marketing-video"
                id={video ? `marketing-video-${video}` : undefined}
                load
                loop
                play={isVisible || !video}
                timer={0}
                renderImage={renderImage}
                video={video}
                image={markingImage}
                videoPosterStyle="marketing-video"
              />
            </div>
            <div className={Style.shadow} />
          </div>
        </div>
      </Link>
    </VisibilitySensor>
  );
};

export default MarketingBanner;

MarketingBanner.propTypes = {
  alternativeHeadline: PropTypes.string.isRequired,
  adjacentCollectionAbove: PropTypes.string.isRequired,
  autoPlayStill: PropTypes.shape({}).isRequired,
  autoPlayVideo: PropTypes.shape({}).isRequired,
  bannerDimensions: PropTypes.string.isRequired,
  breakpoint: PropTypes.string,
  ctaText: PropTypes.string.isRequired,
  headline: PropTypes.string,
  images: PropTypes.shape({}).isRequired,
  name: PropTypes.string,
  url: PropTypes.string.isRequired,
};
